import type { ReactElement } from 'react';

/**
 * Kur kartindaki bayrak. EMOJI BAYRAK KULLANILMADI: Windows/Chrome emoji
 * bayragi cizmez, "US" gibi harf ciftine dusurur. Gosterilen para birimi
 * seti sabit ve kucuk (DISPLAYED_CURRENCY_CODES); inline SVG hem her
 * platformda ayni gorunur hem bagimlilik/istek getirmez.
 *
 * `preserveAspectRatio="slice"` bayragi kare rozete TASIRARAK sigdirir —
 * 3:2 bayragin kenarlarindan kirpilir, rozet bos serit birakmaz.
 */

const EU_STAR =
  'M0,-1 L0.225,-0.309 0.951,-0.309 0.363,0.118 0.588,0.809 0,0.382 -0.588,0.809 -0.363,0.118 -0.951,-0.309 -0.225,-0.309 Z';

/** 12 yildizin merkezleri: (30,20) etrafinda 12 yaricapli cember, 30 derece adim. */
const EU_STAR_CENTERS = Array.from({ length: 12 }, (_, k) => {
  const angle = (k * 30 * Math.PI) / 180;
  return [30 + 12 * Math.sin(angle), 20 - 12 * Math.cos(angle)] as const;
});

const FLAGS: Record<string, ReactElement> = {
  USD: (
    <>
      <rect width="60" height="40" fill="#b22234" />
      <g fill="#fff">
        {/* 13 serit -> serit boyu 40/13; beyazlar tek indeksli seritler. */}
        {[1, 3, 5, 7, 9, 11].map((i) => (
          <rect key={i} y={(40 / 13) * i} width="60" height={40 / 13} />
        ))}
      </g>
      <rect width="24" height={(40 / 13) * 7} fill="#3c3b6e" />
      <g fill="#fff">
        {/* Yildiz alani rozet boyutunda noktaya indirgendi — 50 yildiz cizilmez. */}
        {[0, 1, 2, 3, 4].flatMap((row) =>
          [0, 1, 2, 3, 4].map((col) => (
            <circle
              key={`${row}-${col}`}
              cx={2.6 + col * 4.4 + (row % 2) * 2.2}
              cy={2.6 + row * 3.8}
              r="0.9"
            />
          )),
        )}
      </g>
    </>
  ),
  EUR: (
    <>
      <rect width="60" height="40" fill="#039" />
      <g fill="#fc0">
        {EU_STAR_CENTERS.map(([cx, cy], k) => (
          <path key={k} d={EU_STAR} transform={`translate(${cx} ${cy}) scale(2.2)`} />
        ))}
      </g>
    </>
  ),
  GBP: (
    <>
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="8" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#c8102e" strokeWidth="3.2" />
      <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="13" />
      <path d="M30,0 V40 M0,20 H60" stroke="#c8102e" strokeWidth="8" />
    </>
  ),
  CAD: (
    <>
      <rect width="60" height="40" fill="#fff" />
      <rect width="15" height="40" fill="#d52b1e" />
      <rect x="45" width="15" height="40" fill="#d52b1e" />
      <polygon
        fill="#d52b1e"
        points="30,9.5 31.6,12.8 33.9,11.6 33.3,15.2 37.2,14.6 35.6,17.6 39.5,19.4 35.9,21 37.3,24.1 33.3,23.2 33.6,26.8 30.6,24.9 30.6,30.5 29.4,30.5 29.4,24.9 26.4,26.8 26.7,23.2 22.7,24.1 24.1,21 20.5,19.4 24.4,17.6 22.8,14.6 26.7,15.2 26.1,11.6 28.4,12.8"
      />
    </>
  ),
};

type CurrencyFlagProps = {
  /** ISO para kodu (USD/EUR/GBP/CAD). Bilinmeyen kod eski davranisa duser: kodun kendisi. */
  code: string;
};

export function CurrencyFlag({ code }: CurrencyFlagProps) {
  const flag = FLAGS[code];
  if (!flag) return <>{code}</>;
  return (
    /* Ciftin adi ("USD/TRY") hemen yaninda yazili; bayrak dekoratif. */
    <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {flag}
    </svg>
  );
}
